<template>
  <div class="gestionale-container">
    <!-- Top Bar: Search & Category Filter (Hick-Hyman Law optimization) -->
    <div class="gestionale-control-bar">
      <div class="search-box">
        <i class="fas fa-search search-icon"></i>
        <input 
          type="text" 
          v-model="searchQuery" 
          :placeholder="$t('cerca_modulo', 'Cerca modulo o operatività...')" 
          class="sci-input search-input"
        />
        <button 
          v-if="searchQuery" 
          type="button" 
          class="btn-clear-search" 
          @click="searchQuery = ''"
          aria-label="Pulisci ricerca"
        >
          <i class="fas fa-times"></i>
        </button>
      </div>

      <div class="category-filters" role="tablist">
        <button 
          type="button"
          class="filter-chip" 
          :class="{ active: selectedCategory === 'all' }"
          @click="selectedCategory = 'all'"
        >
          <i class="fas fa-th-large me-1"></i>
          <span>Tutti ({{ filteredPermessi.length }})</span>
        </button>
        <button 
          v-for="cat in availableCategories" 
          :key="cat.id"
          type="button"
          class="filter-chip"
          :class="{ active: selectedCategory === cat.id }"
          @click="selectedCategory = cat.id"
        >
          <i :class="cat.icon" class="me-1"></i>
          <span>{{ cat.label }}</span>
        </button>
      </div>
    </div>

    <!-- Empty State if search yields no results -->
    <div v-if="filteredPermessi.length === 0" class="empty-state-card sci-card">
      <i class="fas fa-search-minus empty-icon"></i>
      <h3 class="empty-title">Nessun modulo trovato</h3>
      <p class="empty-desc">Nessuna funzionalità corrisponde a "{{ searchQuery }}". Prova con altri termini o resetta la ricerca.</p>
      <button type="button" class="sci-btn sci-btn-primary" @click="resetFilters">
        Mostra tutti i moduli
      </button>
    </div>

    <!-- Bento Spatial Grid with Grouped Modules -->
    <div v-else class="bento-modules-wrapper">
      <div 
        v-for="group in groupedDisplay" 
        :key="group.id" 
        class="module-group-section"
      >
        <div class="group-header" v-if="selectedCategory === 'all'">
          <div class="group-title-badge">
            <i :class="group.icon" class="group-icon"></i>
            <h2 class="group-title">{{ group.label }}</h2>
          </div>
          <span class="group-count">{{ group.items.length }} moduli</span>
        </div>

        <div class="bento-grid">
          <div 
            v-for="permesso in group.items" 
            :key="permesso.id || permesso.event"
            class="bento-card"
            :class="getCardAccentClass(permesso.event)"
            @click="executeEvent(permesso.event)"
            tabindex="0"
            role="button"
            :aria-label="$t(permesso.event)"
            @keyup.enter="executeEvent(permesso.event)"
          >
            <div class="card-icon-wrapper">
              <i :class="getEventIcon(permesso.event)"></i>
            </div>
            <div class="card-content">
              <h3 class="card-title">{{ $t(permesso.event) }}</h3>
              <span class="card-category-tag">{{ group.label }}</span>
            </div>
            <div class="card-arrow">
              <i class="fas fa-arrow-right"></i>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { store } from '../store/store.js'

export default {
  name: 'Gestionale',
  data: function () {
    return {
      permessi: [],
      searchQuery: '',
      selectedCategory: 'all',
      store,
      categoryDefs: [
        {
          id: 'service',
          label: 'Sala & Comande',
          icon: 'fas fa-utensils',
          events: ['ordina', 'cameriere', 'marcia', 'tempiDiAttesa', 'prenotazioniSetting']
        },
        {
          id: 'menu',
          label: 'Menù & Prodotti',
          icon: 'fas fa-book-open',
          events: ['editMenu', 'mappaturaPiatti', 'mappaturaP', 'tabellaProdotti', 'aggiunte', 'allergeni', 'traduci', 'digitalizzazione']
        },
        {
          id: 'logistics',
          label: 'Asporto & Magazzino',
          icon: 'fas fa-boxes',
          events: ['gestioneAsporto', 'magazzino', 'carica']
        },
        {
          id: 'finance',
          label: 'Cassa & Fiscale',
          icon: 'fas fa-file-invoice-dollar',
          events: ['documenti-fiscali', 'gestioneFatture', 'pagamenti', 'stampaPrenotati']
        },
        {
          id: 'admin',
          label: 'Statistiche & Setup',
          icon: 'fas fa-sliders-h',
          events: [
            'statistiche', 'riepilogo', 'riepilogoAndamento', 'report-ai',
            'TIMBRATURE', 'NEWSLETTER', 'stampanti', 'impostazioni', 'settings',
            'gestioneCredenziali', 'changelayout', 'gestioneSocial'
          ]
        }
      ],
      iconMap: {
        'editMenu': 'fas fa-book-open',
        'mappaturaP': 'fas fa-layer-group',
        'tabellaProdotti': 'fas fa-table',
        'magazzino': 'fas fa-boxes',
        'mappaturaPiatti': 'fas fa-sitemap',
        'ordina': 'fas fa-cash-register',
        'carica': 'fas fa-truck-loading',
        'analisiCosti': 'fas fa-coins',
        'riepilogoAndamento': 'fas fa-chart-line',
        'aggiunte': 'fas fa-plus-circle',
        'stampaPrenotati': 'fas fa-print',
        'gestioneCredenziali': 'fas fa-key',
        'changelayout': 'fas fa-palette',
        'gestioneAsporto': 'fas fa-motorcycle',
        'stampanti': 'fas fa-print',
        'gestioneFatture': 'fas fa-file-invoice',
        'gestioneSocial': 'fas fa-share-alt',
        'report-ai': 'fas fa-robot',
        'traduci': 'fas fa-language',
        'allergeni': 'fas fa-shield-alt',
        'pagamenti': 'fas fa-credit-card',
        'digitalizzazione': 'fas fa-qrcode',
        'tempiDiAttesa': 'fas fa-clock',
        'prenotazioniSetting': 'fas fa-calendar-check',
        'marcia': 'fas fa-bell',
        'cameriere': 'fas fa-concierge-bell',
        'settings': 'fas fa-cog',
        'impostazioni': 'fas fa-cog',
        'riepilogo': 'fas fa-clipboard-list',
        'statistiche': 'fas fa-chart-pie',
        'documenti-fiscali': 'fas fa-receipt',
        'TIMBRATURE': 'fas fa-user-clock',
        'NEWSLETTER': 'fas fa-envelope-open-text'
      }
    }
  },
  computed: {
    availableCategories() {
      return this.categoryDefs.filter(cat => {
        return this.permessi.some(p => cat.events.includes(p.event));
      });
    },
    filteredPermessi() {
      let list = this.permessi;
      if (this.selectedCategory !== 'all') {
        const cat = this.categoryDefs.find(c => c.id === this.selectedCategory);
        if (cat) {
          list = list.filter(p => cat.events.includes(p.event));
        }
      }
      if (this.searchQuery && this.searchQuery.trim() !== '') {
        const query = this.searchQuery.toLowerCase().trim();
        list = list.filter(p => {
          const translated = (this.$t(p.event) || '').toLowerCase();
          const eventName = (p.event || '').toLowerCase();
          return translated.includes(query) || eventName.includes(query);
        });
      }
      return list;
    },
    groupedDisplay() {
      if (this.selectedCategory !== 'all') {
        const cat = this.categoryDefs.find(c => c.id === this.selectedCategory);
        return [{
          id: cat ? cat.id : 'custom',
          label: cat ? cat.label : 'Moduli',
          icon: cat ? cat.icon : 'fas fa-cubes',
          items: this.filteredPermessi
        }];
      }

      const groups = [];
      const assignedEvents = new Set();

      this.categoryDefs.forEach(cat => {
        const matched = this.filteredPermessi.filter(p => cat.events.includes(p.event));
        if (matched.length > 0) {
          groups.push({
            id: cat.id,
            label: cat.label,
            icon: cat.icon,
            items: matched
          });
          matched.forEach(p => assignedEvents.add(p.event));
        }
      });

      const unassigned = this.filteredPermessi.filter(p => !assignedEvents.has(p.event));
      if (unassigned.length > 0) {
        groups.push({
          id: 'other',
          label: 'Altri Moduli',
          icon: 'fas fa-cubes',
          items: unassigned
        });
      }

      return groups;
    }
  },
  methods: {
    displayUser() {
      const storedNome = localStorage.nome ? JSON.parse(localStorage.nome) : '';
      const storedToken = localStorage.token ? JSON.parse(localStorage.token) : '';

      this.axios.post(this.store.path + 'displayUser', {
        nome: storedNome,
        headers: {
          authorization: `Bearer ${storedToken}`
        }
      }).then((response) => {
        if (response.data && response.data.messaggio) {
          const valid = [];
          response.data.messaggio.forEach(element => {
            if (element.event) {
              valid.push(element);
            }
          });
          this.permessi = valid.sort((a, b) => (a.event > b.event) ? 1 : ((b.event > a.event) ? -1 : 0));
        } else {
          console.log('Non ci sono permessi da mostrare');
        }
      }).catch(err => {
        console.error("Errore recupero permessi:", err);
      });
    },
    resetFilters() {
      this.searchQuery = '';
      this.selectedCategory = 'all';
    },
    getEventIcon(event) {
      return this.iconMap[event] || 'fas fa-cube';
    },
    getCardAccentClass(event) {
      if (['ordina', 'cameriere', 'marcia'].includes(event)) return 'accent-service';
      if (['editMenu', 'mappaturaPiatti', 'aggiunte'].includes(event)) return 'accent-menu';
      if (['gestioneAsporto', 'magazzino'].includes(event)) return 'accent-logistics';
      if (['documenti-fiscali', 'pagamenti', 'gestioneFatture'].includes(event)) return 'accent-finance';
      return 'accent-default';
    },
    executeEvent(eventName) {
      if (typeof this[eventName] === 'function') {
        this[eventName]();
      } else {
        console.warn(`Metodo non trovato per evento: ${eventName}`);
      }
    },
    goTo(e) {
      const id = e.currentTarget.id || e.target.id;
      this.executeEvent(id);
    },
    editMenu() {
      this.$router.push({ name: "EditMenu" });
    },
    mappaturaP() {
      this.$router.push({ name: "MappaturaP" });
    },
    tabellaProdotti() {
      this.$router.push({ name: "TabellaProdotti" });
    },
    magazzino() {
      this.$router.push({ name: "Magazzino" });
    },
    mappaturaPiatti() {
      this.$router.push({ name: "MappaturaPiatti" });
    },
    ordina() {
      this.$router.push({ name: "Ordina" });
    },
    carica() {
      this.$router.push({ name: "Carica" });
    },
    analisiCosti() {
      this.$router.push({ name: "analisiCosti" });
    },
    riepilogoAndamento() {
      this.$router.push({ name: "riepilogoAndamento" });
    },
    aggiunte() {
      this.$router.push({ name: "aggiunte" });
    },
    stampaPrenotati() {
      this.$router.push({ name: "stampaPrenotati" });
    },
    gestioneCredenziali() {
      this.$router.push({ name: "GestioneCredenziali" });
    },
    changelayout() {
      this.$router.push({ name: "ChangeLayout" });
    },
    gestioneAsporto() {
      this.$router.push({ name: "GestioneAsporto" });
    },
    stampanti() {
      this.$router.push({ name: "Stampanti" });
    },
    gestioneFatture() {
      this.$router.push({ name: "InvoiceManager" });
    },
    gestioneSocial() {
      this.$router.push({ name: "Social" });
    },
    "report-ai"() {
      this.$router.push({ name: "ReportAI" });
    },
    traduci() {
      this.$router.push({ name: "Traduci" });
    },
    allergeni() {
      this.$router.push({ name: "Allergeni" });
    },
    pagamenti() {
      this.$router.push({ name: "Pagamenti" });
    },
    digitalizzazione() {
      this.$router.push({ name: "Digitalizzazione" });
    },
    tempiDiAttesa() {
      this.$router.push({ name: "TempiDiAttesa" });
    },
    prenotazioniSetting() {
      this.$router.push({ name: "PrenotazioniSetting" });
    },
    marcia() {
      this.$router.push({ name: "Marcia" });
    },
    cameriere() {
      this.$router.push({ name: "ChiamateCameriere" });
    },
    settings() {
      this.$router.push({ name: "Impostazioni" });
    },
    impostazioni() {
      this.$router.push({ name: "Impostazioni" });
    },
    riepilogo() {
      this.$router.push({ name: "RiepilogoOrdini" });
    },
    statistiche() {
      this.$router.push({ name: "Statistiche" });
    },
    "documenti-fiscali"() {
      this.$router.push({ name: "DocumentiFiscali" });
    },
    TIMBRATURE() {
      this.$router.push({ name: "GestioneTimbrature" });
    },
    NEWSLETTER() {
      this.$router.push({ name: "Newsletter" });
    }
  },
  beforeMount() {
    this.displayUser();
  }
}
</script>

<style scoped>
.gestionale-container {
  max-width: 1380px;
  margin: 0 auto;
  padding: 24px 20px 48px;
  box-sizing: border-box;
}

/* Control Bar: Search & Category Chips */
.gestionale-control-bar {
  display: flex;
  flex-direction: column;
  gap: 16px;
  margin-bottom: 32px;
  background: #ffffff;
  padding: 16px 20px;
  border-radius: var(--radius-lg, 16px);
  border: 1px solid var(--sci-border, #e2e8f0);
  box-shadow: 0 4px 12px rgba(15, 23, 42, 0.03);
}

.search-box {
  position: relative;
  width: 100%;
}

.search-icon {
  position: absolute;
  left: 16px;
  top: 50%;
  transform: translateY(-50%);
  color: #94a3b8;
  font-size: 15px;
  pointer-events: none;
}

.search-input {
  padding-left: 44px;
  padding-right: 40px;
  height: 48px;
  font-size: 15px;
  border-radius: 12px;
  background-color: var(--sci-bg-subtle, #f8fafc);
  border: 1px solid var(--sci-border, #e2e8f0);
  transition: all 0.2s ease;
}

.search-input:focus {
  background-color: #ffffff;
  border-color: var(--sci-primary, #4f46e5);
}

.btn-clear-search {
  position: absolute;
  right: 12px;
  top: 50%;
  transform: translateY(-50%);
  border: none;
  background: transparent;
  color: #94a3b8;
  padding: 6px 10px;
  cursor: pointer;
}

.btn-clear-search:hover {
  color: #0f172a;
}

.category-filters {
  display: flex;
  align-items: center;
  gap: 8px;
  overflow-x: auto;
  padding-bottom: 4px;
  scrollbar-width: thin;
}

.filter-chip {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  min-height: 38px;
  padding: 6px 14px;
  border-radius: 9999px;
  border: 1px solid var(--sci-border, #e2e8f0);
  background: var(--sci-bg-subtle, #f8fafc);
  color: var(--sci-text-muted, #475569);
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  white-space: nowrap;
  transition: all 0.2s cubic-bezier(0.22, 1, 0.36, 1);
}

.filter-chip:hover {
  border-color: #cbd5e1;
  color: #0f172a;
  background: #ffffff;
}

.filter-chip.active {
  background: var(--sci-primary, #4f46e5);
  border-color: var(--sci-primary, #4f46e5);
  color: #ffffff;
  box-shadow: 0 2px 8px rgba(79, 70, 229, 0.3);
}

/* Group Section */
.module-group-section {
  margin-bottom: 32px;
}

.group-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 16px;
  padding-bottom: 8px;
  border-bottom: 2px solid var(--sci-border, #e2e8f0);
}

.group-title-badge {
  display: flex;
  align-items: center;
  gap: 10px;
}

.group-icon {
  font-size: 18px;
  color: var(--sci-primary, #4f46e5);
}

.group-title {
  font-size: 17px;
  font-weight: 700;
  letter-spacing: -0.01em;
  color: var(--sci-text-main, #0f172a);
  margin: 0;
}

.group-count {
  font-size: 12px;
  font-weight: 600;
  color: #64748b;
  background: #f1f5f9;
  padding: 3px 8px;
  border-radius: 9999px;
}

/* Bento Grid */
.bento-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  gap: 16px;
}

.bento-card {
  display: flex;
  align-items: center;
  gap: 16px;
  min-height: 84px;
  padding: 16px 18px;
  background: #ffffff;
  border: 1px solid var(--sci-border, #e2e8f0);
  border-radius: var(--radius-md, 14px);
  box-shadow: 0 2px 4px rgba(15, 23, 42, 0.03);
  cursor: pointer;
  transition: all 0.22s cubic-bezier(0.22, 1, 0.36, 1);
  position: relative;
  overflow: hidden;
  user-select: none;
}

.bento-card:hover {
  transform: translateY(-3px);
  border-color: #cbd5e1;
  box-shadow: 0 8px 18px rgba(15, 23, 42, 0.08);
}

.bento-card:active {
  transform: scale(0.98);
}

.bento-card:focus-visible {
  outline: 2px solid var(--sci-primary, #4f46e5);
  outline-offset: 2px;
}

.card-icon-wrapper {
  width: 48px;
  height: 48px;
  min-width: 48px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 20px;
  background: #f1f5f9;
  color: #334155;
  transition: all 0.2s ease;
}

.card-content {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 2px;
  text-align: left;
}

.card-title {
  font-size: 14.5px;
  font-weight: 700;
  line-height: 1.25;
  color: var(--sci-text-main, #0f172a);
  margin: 0;
}

.card-category-tag {
  font-size: 11px;
  font-weight: 500;
  color: #64748b;
  text-transform: uppercase;
  letter-spacing: 0.04em;
}

.card-arrow {
  color: #cbd5e1;
  font-size: 13px;
  transition: all 0.2s ease;
}

.bento-card:hover .card-arrow {
  color: var(--sci-primary, #4f46e5);
  transform: translateX(4px);
}

/* Card Accents */
.bento-card.accent-service .card-icon-wrapper {
  background: var(--sci-emerald-subtle, #ecfdf5);
  color: var(--sci-emerald, #059669);
}

.bento-card.accent-menu .card-icon-wrapper {
  background: var(--sci-primary-subtle, #eef2ff);
  color: var(--sci-primary, #4f46e5);
}

.bento-card.accent-logistics .card-icon-wrapper {
  background: var(--sci-sky-subtle, #f0f9ff);
  color: var(--sci-sky, #0284c7);
}

.bento-card.accent-finance .card-icon-wrapper {
  background: var(--sci-amber-subtle, #fffbeb);
  color: var(--sci-amber, #d97706);
}

.bento-card.accent-default .card-icon-wrapper {
  background: #f8fafc;
  color: #475569;
}

/* Empty State */
.empty-state-card {
  padding: 48px 24px;
  text-align: center;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
}

.empty-icon {
  font-size: 40px;
  color: #94a3b8;
  margin-bottom: 8px;
}

.empty-title {
  font-size: 18px;
  font-weight: 700;
  margin: 0;
}

.empty-desc {
  color: #64748b;
  max-width: 440px;
  margin: 0 0 16px;
  font-size: 14px;
}

@media (max-width: 600px) {
  .gestionale-container {
    padding: 16px 12px 32px;
  }
  .bento-grid {
    grid-template-columns: 1fr;
  }
  .bento-card {
    min-height: 72px;
    padding: 12px 14px;
  }
}
</style>
